import type { TaskType } from '~/types/tasks';

// TODO: надо вынести в типы и типизировать все
export enum Level {
  easy = 1,
  average = 2,
  hot = 3,
}

export enum Sex {
  boy = 'boy',
  girl = 'girl',
}

export enum SexMapper {
  m = 'boy',
  f = 'girl',
}

type FetchTaskParametersType = {
  target: Sex;
  level: Level;
  boyFinished: boolean;
  girlFinished: boolean;
  categories: number[];
};

export const useGameStore = defineStore('game', () => {
  const infoStore = useInfoStore();
  const mainStore = useMainStore();
  const route = useRoute();
  const categories = useCookie<number[]>('categories');

  const isNotFoundTask = ref(false);
  const isFinished = ref(false);
  const lastTime = ref<number>();

  const prefetchedTaskData = ref<[FetchTaskParametersType, TaskType]>();

  const task = useState<TaskType | null>('task', () => null);

  const level = computed<Level>(
    () => +(route.query.level || task.value?.level || Level.easy) as Level,
  );
  const nextTarget = computed(
    () =>
      ({ [Sex.girl]: Sex.boy, [Sex.boy]: Sex.girl })[
        (task.value?.target || Sex.boy) as Sex
      ],
  );

  const isPairedMode = computed(() => route.query.mode === 'paired');
  const isPairedGameLeader = computed(
    () => isPairedMode.value && infoStore.pairedGame?.leaded,
  );
  const isYourTurn = computed(() =>
    task.value
      ? task.value.target === SexMapper[mainStore.user.sex]
      : isPairedGameLeader.value,
  );

  const fetchTaskParameters = computed<FetchTaskParametersType>(() => ({
    target: nextTarget.value,
    level: level.value,
    boyFinished: isFinished.value,
    girlFinished: isFinished.value,
    categories: categories.value,
  }));

  const prefetchTask = () => {
    if (isPairedMode.value || !task.value) return;

    TaskAPI.random({
      query: {
        ...fetchTaskParameters.value,
        completed: [
          ...JSON.parse(localStorage.getItem('completed_tasks') || '[]'),
          task.value.id,
        ],
      },
    }).then((data) => {
      prefetchedTaskData.value = [fetchTaskParameters.value, data];
    });
  };

  const onTaskLoaded = (data: TaskType) => {
    isNotFoundTask.value = false;

    task.value = data;

    if (isPairedMode.value)
      infoStore.sendPartnerTask(isYourTurn.value ? null : data);

    prefetchTask();
  };

  const fetchTask = async () => {
    if (
      prefetchedTaskData.value &&
      Object.entries(fetchTaskParameters.value).every(
        ([key, value]) =>
          prefetchedTaskData.value?.[0][
            key as keyof FetchTaskParametersType
          ] === value,
      )
    ) {
      onTaskLoaded(prefetchedTaskData.value[1]);

      return;
    }

    return TaskAPI.random({
      query: {
        ...fetchTaskParameters.value,
        completed: JSON.parse(localStorage.getItem('completed_tasks') || '[]'),
      },
    })
      .then(onTaskLoaded)
      .catch(() => {
        task.value = null;
        isNotFoundTask.value = true;

        MetricAPI.create({
          body: {
            type: 'not_enough_tasks',
            data: { categories: categories.value },
          },
        });
      });
  };

  const getRandomTask = () => {
    if (isPairedMode.value && !isPairedGameLeader.value) return;

    if (lastTime.value && task.value) {
      MetricAPI.create({
        body: {
          type: 'task_completed',
          data: {
            duration: Date.now() - lastTime.value,
            id: task.value.id,
            mode: isPairedMode.value ? 'paired' : 'single',
          },
        },
      });
    }
    lastTime.value = Date.now();

    localStorage.setItem(
      'completed_tasks',
      JSON.stringify(
        JSON.parse(localStorage.getItem('completed_tasks') || '[]').concat(
          task.value ? [task.value.id] : [],
        ),
      ),
    );

    return fetchTask();
  };

  const reset = () => {
    task.value = null;
    isNotFoundTask.value = false;
    localStorage.removeItem('completed_tasks');
  };

  const clear = () => {
    task.value = null;
    isFinished.value = false;
    reset();
  };

  return {
    task,
    level,
    isNotFoundTask,
    isFinished,
    prefetchedTaskData,
    categories,

    isPairedMode,
    isPairedGameLeader,
    isYourTurn,

    getRandomTask,
    reset,
    clear,
  };
});
